/**
 * Array of transformer functions that run before saving a recipe.
 * Each transformer has a name and function that takes recipe, nodes and edges as input.
 * The transformer function should return the modified recipe.
 * Any validation errors are caught and stored to be displayed in the UI.
 */
import { discardLayoutInfo } from './discardLayoutInfo';
import { reOrderNodes } from './reOrderNodes';
import { removeEmptySchema } from './removeEmptySchema';
import { removeNullishValues } from './removeNullishValues';

export const categories = [
  ['connectors', 'connector'],
  ['writeFormats', 'writeFormat'],
  ['readFormats', 'readFormat'],
  ['tasks', 'task'],
  ['imports', 'import'],
  ['statements', 'statement'],
];

/**
 * The transformers run in sequence, so order is important.
 * Later transformers operate on the recipe returned by earlier ones.
 */
const transformers = [
  {
    name: 'reOrderNodes',
    transformer: ({ recipe, nodes, edges }) => {
      return reOrderNodes({ recipe, nodes, edges });
    },
  },
  {
    name: 'discardLayoutInfo',
    transformer: ({ recipe, nodes, edges }) => {
      return discardLayoutInfo({ recipe, nodes, edges });
    },
  },
  // Removed removeEmptyParameters - covered by removeNullishValues
  // {
  //   name: "removeEmptyParameters",
  //   transformer: ({ recipe, nodes, edges }) => {
  //     return removeEmptyParameters({ recipe });
  //   },
  // },
  {
    name: 'removeNullishValues',
    transformer: ({ recipe, nodes, edges }) => {
      // You can configure the flag here based on your requirements
      // For now, setting it to false as default behavior
      return removeNullishValues({
        recipe,
        removeKeysWithObjectButAllNullishValues: false,
      });
    },
  },

  {
    name: 'removeEmptySchema',
    transformer: ({ recipe, nodes, edges }) => {
      return removeEmptySchema({ recipe });
    },
  },
];

/**
 * Runs transformers and validators on a recipe before it is saved.
 * These are runtime transformations separate from schema validation.
 *
 * @param {Object} recipe - The recipe to transform
 * @param {Array} nodes - The current nodes (for reference only)
 * @param {Array} edges - The current edges (for reference only)
 * @returns {Object} Result containing:
 *   - isError: Boolean indicating if any transformers failed
 *   - errors: Array of caught errors from transformers
 *   - recipe: The transformed recipe
 */
export const preSaveTransformers = ({ recipe, nodes, edges }) => {
  const res = {
    isError: false,
    errors: [],
    recipe,
  };
  transformers.forEach((transformer) => {
    try {
      const transformedRecipe = transformer.transformer({
        recipe: res.recipe, // Use the accumulated transformed recipe, not the original
        nodes,
        edges,
      });

      res.recipe = transformedRecipe;
    } catch (error) {
      res.isError = true;
      res.errors.push(error);
    }
  });

  return res;
};
